import { PARTNER_AGREEMENT_VERSION } from '@zuruflex/types';
import { cancellationFeeRate, FREE_CANCELLATION_HOURS } from './booking-rules.js';
/*
 * ── the cancellation table, read out of the booking rules ──────────────
 *
 * `cancellationFeeRate` is what actually charges the customer. Writing the
 * same tiers out again in prose is how a published policy quietly stops
 * matching the code, so the table below is *probed* out of that function
 * instead of transcribed from it: change the rates in `@zuruflex/core` and
 * this page changes with them.
 */
const PROBE_STEP_MINUTES = 5;
const PROBE_CEILING_HOURS = FREE_CANCELLATION_HOURS + 2;
/** The fee for cancelling this many hours ahead of pick-up. */
function rateAt(hoursBeforePickup) {
    const now = new Date(0);
    return cancellationFeeRate(new Date(hoursBeforePickup * 3_600_000), now);
}
function cancellationBands() {
    const bands = [];
    let start = 0;
    let rate = rateAt(0);
    // Stepped in whole minutes rather than fractional hours: accumulating a
    // 1/12 float three hundred times lands a boundary at 23.999 hours.
    for (let minutes = PROBE_STEP_MINUTES; minutes <= PROBE_CEILING_HOURS * 60; minutes += PROBE_STEP_MINUTES) {
        const hours = minutes / 60;
        const next = rateAt(hours);
        if (next === rate)
            continue;
        bands.push({ fromHours: start, toHours: hours, rate });
        start = hours;
        rate = next;
    }
    bands.push({ fromHours: start, toHours: null, rate });
    // Most generous first: that is the order somebody reads the table in.
    return bands.reverse();
}
const hours = (value) => (Number.isInteger(value) ? String(value) : value.toFixed(1));
function bandRow(band) {
    const term = band.toHours === null
        ? `${hours(band.fromHours)} hours or more before pick-up`
        : band.fromHours === 0
            ? `Less than ${hours(band.toHours)} hours before pick-up, or after it`
            : `Between ${hours(band.fromHours)} and ${hours(band.toHours)} hours before pick-up`;
    return {
        term,
        detail: band.rate === 0
            ? 'No fee. The full amount is refunded.'
            : `${Math.round(band.rate * 100)}% of the booking total is kept.`,
    };
}
export const CANCELLATION_BANDS = cancellationBands();
/* ── the documents ─────────────────────────────────────────────────── */
const TERMS = {
    slug: 'terms',
    version: null,
    title: 'Rental terms',
    lead: 'The agreement between you and Zuruflex when you book a car through us.',
    updated: '2026-09-19',
    draft: true,
    sections: [
        {
            heading: 'Who you are contracting with',
            body: [
                'Zuruflex operates car rental in Accra, Ghana and Kigali, Rwanda. The registered entity, company number and registered address for each country are to be confirmed before this page is published.',
                'Some cars in the fleet are owned by partner operators rather than by Zuruflex. Where that is the case, Zuruflex arranges and services the booking and remains your point of contact, and the partner supplies the vehicle.',
            ],
        },
        {
            heading: 'Making a booking',
            body: [
                'A booking starts as a request. It is not confirmed until we tell you it is confirmed, and a car is not held for you before that point.',
                'We may decline a request — most often because the car has just gone out, or because we could not verify a driving licence in time.',
            ],
        },
        {
            heading: 'Who may drive',
            body: [
                'Only the driver named on the booking may drive the car. Minimum age, minimum licence-held period and accepted licence types are to be confirmed.',
                'We check the driving licence before the keys are handed over. If the licence does not match the named driver, the rental does not start and the cancellation terms apply.',
            ],
        },
        {
            heading: 'Price, payment and deposit',
            body: [
                'The price quoted at the time of booking is the price for the dates quoted. Payment is taken by card or mobile money, either through our payment provider or, where we say so at checkout, by mobile money sent directly to the Zuruflex number shown — in which case the booking is confirmed once we have checked the payment.',
                'A refundable deposit may be held against damage, fuel and traffic fines. The amount, and how long it takes to return, are to be confirmed.',
            ],
        },
        {
            heading: 'Using the car',
            body: [
                'The car is for ordinary private use on public roads, by the named driver only. It may not be sublet, used for hire or reward, raced, or taken outside the country of rental without written agreement.',
                'Fuel policy, mileage allowance and any geographic limits are shown on the booking. Traffic fines, tolls and parking charges incurred during the rental are yours.',
            ],
        },
        {
            heading: 'Damage, breakdown and accidents',
            body: [
                'Tell us as soon as anything happens to the car. What is covered, and the excess you are liable for, is set out on the insurance page.',
            ],
        },
        {
            heading: 'Returning the car',
            body: [
                'Return the car to the agreed place at the agreed time, with the agreed fuel level. Late returns are charged; the rate is to be confirmed.',
            ],
        },
        {
            heading: 'Cancelling',
            body: ['Cancellation and refunds are covered in full on the cancellation page.'],
        },
        {
            heading: 'Liability',
            body: ['To be drafted with counsel.'],
        },
        {
            heading: 'Governing law and disputes',
            body: [
                'Bookings in Ghana are governed by Ghanaian law and bookings in Rwanda by Rwandan law. The dispute procedure is to be confirmed.',
            ],
        },
        {
            heading: 'Changes to these terms',
            body: [
                'We may update these terms. The terms that apply to your booking are the ones published when you booked, and the date at the top of this page is when it last changed.',
            ],
        },
    ],
};
const PRIVACY = {
    slug: 'privacy',
    version: null,
    title: 'Privacy policy',
    lead: 'What we collect when you rent a car from us, why we hold it, and what you can ask us to do with it.',
    updated: '2026-10-05',
    draft: true,
    sections: [
        {
            heading: 'What we collect',
            body: ['Most of this you give us yourself, in the chat or on the booking form.'],
            rows: [
                { term: 'Who you are', detail: 'Name, phone number, email address.' },
                {
                    term: 'Driver documents',
                    detail: 'Driving licence and, where required, a national ID or passport, so we can confirm you may drive the car.',
                },
                {
                    term: 'Your messages',
                    detail: 'The WhatsApp conversation you book through, kept so the next person who picks up your booking can see what was agreed.',
                },
                {
                    term: 'Booking and payment',
                    detail: 'Dates, pick-up point, the car, and a reference from our payment provider. Zuruflex does not store your card number.',
                },
            ],
        },
        {
            heading: 'Why we hold it',
            body: [
                'To fulfil the booking, to confirm the person collecting the car may drive it, to take payment and return deposits, to answer you when something goes wrong, and to meet the record-keeping our regulators and insurers require.',
            ],
        },
        {
            heading: 'Who we share it with',
            body: ['Only where it is needed to run the rental.'],
            rows: [
                {
                    term: 'The car owner',
                    detail: 'Where the car belongs to a partner operator, they receive the driver name and the dates — not your documents.',
                },
                { term: 'Our payment provider', detail: 'To take payment and issue refunds.' },
                { term: 'WhatsApp', detail: 'Because that is where the conversation happens.' },
                {
                    term: 'Authorities',
                    detail: 'Where the law requires it, including traffic and police enquiries about a car during your rental.',
                },
            ],
        },
        {
            heading: 'Where it is stored and for how long',
            body: [
                'Hosting region and retention periods are to be confirmed. Driver documents are held no longer than the record-keeping rules require.',
            ],
        },
        {
            heading: 'Your rights',
            body: [
                'You can ask what we hold about you, ask us to correct it, and ask us to delete what we are not required to keep. Message us and we will action it. How to have your account and data deleted, and what we have to keep, is set out on the account deletion page.',
                'In Ghana these rights sit under the Data Protection Act, 2012 (Act 843) and complaints go to the Data Protection Commission. In Rwanda they sit under Law No. 058/2021 and complaints go to the National Cyber Security Authority. Our registration details with each are to be confirmed.',
            ],
        },
        {
            heading: 'Cookies and analytics',
            body: [
                'This site sets what it needs to remember your language and theme.',
                'We use PostHog, hosted in the EU, to understand how the site is used: which cars people look at, which filters they use and where a booking gets stuck. A banner asks before any of it runs. If you accept, we keep a random identifier on your device and record anonymous sessions of your visit, with everything you type hidden. If you decline, we only count visits, using a daily-rotating identifier worked out on PostHog\'s servers, and nothing is stored on your device. You can change your answer by clearing this site\'s data in your browser.',
                'When you tap a WhatsApp button, the message we prepare ends with a short code such as ZFV-7K2QFA. It lets us tell which page and which car the conversation started from. It is kept for 30 days and carries nothing about you unless you accepted analytics.',
            ],
        },
        {
            heading: 'Contacting us about your data',
            body: [
                'Named contact for data protection to be confirmed. Until then, the address at the foot of this page reaches us.',
            ],
        },
    ],
};
/**
 * How to have an account and its data erased — the address the store
 * listings give as the deletion URL, and what both phone apps open from
 * their Account tab.
 *
 * Not a draft, unlike the privacy notice it hangs off: it describes a
 * process rather than binding anybody to a clause, and the store reviewers
 * who open it need a working page rather than one headed "placeholder".
 * Requests are actioned by staff by hand; there is no self-serve deletion
 * yet, so the page says how to ask rather than offering a button.
 */
const DELETE_ACCOUNT = {
    slug: 'delete-account',
    version: null,
    title: 'Delete your account and data',
    lead: 'How to ask us to close your Zuruflex account and erase the personal data that goes with it, and what we have to keep.',
    updated: '2026-10-13',
    draft: false,
    sections: [
        {
            heading: 'How to ask',
            body: [
                'Message us on WhatsApp or email hello@zuruflex.com and say you want your account deleted. The buttons at the top of this page start the message for you.',
                'Write from the phone number or email address your account uses. That is how we know the request is yours, and it saves us asking you to prove it another way.',
                'This works for both apps and the website, and you do not need the app installed to ask.',
            ],
        },
        {
            heading: 'What we delete',
            body: ['Once the request is confirmed, we erase or anonymise everything below.'],
            rows: [
                { term: 'Your profile', detail: 'Name, phone number, email address, password and any linked Google sign-in.' },
                { term: 'Driver documents', detail: 'Your driving licence and any ID you uploaded.' },
                { term: 'Saved cars and settings', detail: 'Your saved cars, language and notification preferences, and the devices registered for notifications.' },
                { term: 'Messages', detail: 'Your conversations with owners and with us, including photographs you sent.' },
            ],
        },
        {
            heading: 'What we have to keep',
            body: [
                'Some records outlive the account because the law or an open matter requires them. They are kept only for as long as that reason lasts, are no longer linked to an account you can sign in to, and are then deleted.',
            ],
            rows: [
                { term: 'Booking and payment records', detail: 'Dates, amounts and payment references, which tax and accounting rules oblige us to keep.' },
                { term: 'Open hires and disputes', detail: 'Anything tied to a hire that is still running, an unpaid balance, a damage claim or a fine, until it is settled.' },
                { term: 'Reviews', detail: 'Reviews you left stay up, without your name on them.' },
            ],
        },
        {
            heading: 'Partner accounts',
            body: [
                'A business account is deleted at the owner\'s request. Deleting it unlists every car and removes the team\'s logins.',
                'We cannot delete a business while one of its hires is confirmed or under way. Finish or cancel those first, and payouts that are owed are paid before the account closes.',
            ],
        },
        {
            heading: 'How long it takes',
            body: [
                'We confirm we have your request within two working days, and complete it within one month. If something has to wait, such as an open hire, we tell you what and for how long.',
                'Deletion cannot be undone. If you want to use Zuruflex again afterwards, you start a new account.',
            ],
        },
        {
            heading: 'If you are not happy with the answer',
            body: [
                'Your other rights over your data, and where to complain, are set out in the privacy policy.',
            ],
        },
    ],
};
const CANCELLATION = {
    slug: 'cancellation',
    version: null,
    title: 'Cancellation and refunds',
    lead: `Cancel ${FREE_CANCELLATION_HOURS} hours or more before pick-up and it costs you nothing. Closer in, part of the booking is kept.`,
    updated: '2026-09-19',
    draft: true,
    sections: [
        {
            heading: 'How to cancel',
            body: [
                'Tell us in the same conversation you booked in. The cancellation takes effect when we receive the message, not when we reply, so send it as soon as you know.',
            ],
        },
        {
            heading: 'What it costs',
            body: [
                'The fee depends on how long before pick-up you cancel. These are the rates the system actually applies:',
            ],
            rows: CANCELLATION_BANDS.map(bandRow),
        },
        {
            heading: 'How the refund reaches you',
            body: [
                'Refunds go back the way the payment came in — the same card or the same mobile money wallet. How long the provider takes to clear it is to be confirmed.',
                'A refundable deposit is separate from the booking total and is returned in full unless there is damage, missing fuel or a fine against the rental.',
            ],
        },
        {
            heading: 'If we cancel',
            body: [
                'If we cannot supply the car — it is damaged, or the previous rental has not come back — we will offer you the nearest equivalent we have. If nothing works for you, you are refunded in full, whatever the timing.',
            ],
        },
        {
            heading: 'Not turning up',
            body: [
                'If nobody collects the car and we have not heard from you, the booking is treated as cancelled at pick-up time, at the rate in the table above.',
            ],
        },
        {
            heading: 'Changing the dates instead',
            body: [
                'Moving a booking is usually cheaper than cancelling it. Ask — if the car is free on the new dates we will simply move it.',
            ],
        },
    ],
};
const INSURANCE = {
    slug: 'insurance',
    version: null,
    title: 'Insurance and damage',
    lead: 'What the cover on a Zuruflex car includes, what it does not, and what you owe if something happens.',
    updated: '2026-09-19',
    draft: true,
    sections: [
        {
            heading: "What's included",
            body: [
                'Every car on the platform carries the insurance the law requires in its country of registration. The policy type and the insurer for each country are to be confirmed.',
            ],
        },
        {
            heading: 'The excess',
            body: [
                'If the car is damaged, you are liable up to a fixed amount regardless of the cover. That amount is to be confirmed, and will be shown on the booking before you pay.',
            ],
        },
        {
            heading: "What isn't covered",
            body: [
                'To be confirmed with the insurer. Expect the usual exclusions: driving under the influence, an unnamed driver at the wheel, deliberate damage, off-road use, and the interior.',
            ],
        },
        {
            heading: 'If you have an accident',
            body: [
                'Make sure everyone is safe and call the emergency services if anyone is hurt. Then message us before you move the car or agree anything with the other party. Photograph the scene and both vehicles.',
                'A police report is required for any claim in both countries.',
            ],
        },
        {
            heading: 'Breakdowns',
            body: [
                'Message us and we will arrange recovery. If the car cannot be made usable we will get you another one or refund the unused days.',
            ],
        },
        {
            heading: 'Fines and tolls',
            body: [
                'Anything issued against the car while it is with you is yours, including where the notice reaches us months later. We will pass on the notice rather than the guesswork.',
            ],
        },
    ],
};
const PARTNER_TERMS = {
    slug: 'partner-terms',
    version: PARTNER_AGREEMENT_VERSION,
    title: 'Partner terms',
    lead: 'For owners and operators listing their cars on Zuruflex.',
    updated: '2026-09-19',
    draft: true,
    sections: [
        {
            heading: 'Who can list',
            body: [
                'Registered businesses and individual owners in Ghana and Rwanda who can show title to the vehicle and current insurance and roadworthiness.',
            ],
        },
        {
            heading: 'What we verify before you go live',
            body: ['Listings stay hidden until these have been checked.'],
            rows: [
                { term: 'Identity', detail: 'Business registration, or ID for an individual owner.' },
                {
                    term: 'The vehicle',
                    detail: 'Registration document, insurance certificate, roadworthiness certificate.',
                },
                {
                    term: 'Payout details',
                    detail: 'A bank account or mobile money wallet in the name we verified.',
                },
            ],
        },
        {
            heading: 'Keeping the listing honest',
            body: [
                'The car that arrives must be the car in the photographs, in the condition described. Keep the calendar current — a booking we cannot fulfil costs the customer their trip and costs you the listing.',
            ],
        },
        {
            heading: 'Commission and payouts',
            body: ['Commission rate, payout schedule and any hold period are to be confirmed.'],
        },
        {
            heading: 'Condition and maintenance',
            body: [
                'Cars are serviced on schedule, cleaned between rentals, and presented with the fuel level the booking states. Minimum standards are to be confirmed.',
            ],
        },
        {
            heading: 'Damage and claims',
            body: [
                'Damage during a rental is handled through the insurance on the vehicle and the excess held against the renter. The claims process is to be confirmed.',
            ],
        },
        {
            heading: 'Suspension and removal',
            body: [
                'We may hide a listing or suspend an account where documents have expired, a car is repeatedly unavailable, or a customer is put at risk. We will tell you why.',
            ],
        },
        {
            heading: 'Leaving',
            body: [
                'You can withdraw a car at any time, subject to bookings already confirmed on it. Notice periods are to be confirmed.',
            ],
        },
    ],
};
const HELP = {
    slug: 'help',
    version: null,
    title: 'Help and contact',
    lead: 'How to reach a person, and what to do when something is wrong.',
    updated: '2026-09-19',
    draft: false,
    sections: [
        {
            heading: 'Talk to us',
            body: [
                'WhatsApp is the fastest way and the one we watch. Email reaches the same team, more slowly. Both are at the foot of this page.',
                'Branch addresses, phone numbers and opening hours are on the locations page.',
            ],
        },
        {
            heading: 'Before your trip',
            body: [
                'Bring the licence you booked with and the payment method you paid with. If someone else will be driving, tell us before the day — only the named driver may take the car.',
                'Need different dates? Ask rather than cancelling; moving a booking is usually free.',
            ],
        },
        {
            heading: 'During your trip',
            body: [
                'Breakdown, accident or anything that stops you driving: message us straight away and we will arrange recovery or a replacement. If anyone is hurt, call the emergency services first.',
            ],
        },
        {
            heading: 'After your trip',
            body: [
                'Deposits are returned once the car has been checked over. If a fine arrives against your rental afterwards we will send you the notice itself.',
            ],
        },
        {
            heading: 'If we got it wrong',
            body: [
                'Tell us in the chat and ask for it to be escalated. A named complaints route and response time are to be confirmed.',
            ],
        },
    ],
};
/** Every policy page, in the order the footer and the contents list show them. */
export const LEGAL_DOCUMENTS = [
    TERMS,
    PRIVACY,
    DELETE_ACCOUNT,
    CANCELLATION,
    INSURANCE,
    PARTNER_TERMS,
    HELP,
];
export function legalDocument(slug) {
    return LEGAL_DOCUMENTS.find((document) => document.slug === slug);
}
/**
 * Zuruflex's agreement with a partner — the one document in here that is
 * accepted rather than published.
 *
 * Exported by name because the API, the portal and both phone apps all
 * want this one specifically, and finding it by slug in four places is
 * four chances to typo a string the compiler cannot check.
 */
export const PARTNER_AGREEMENT = PARTNER_TERMS;
